import { IconFunnel } from "@btnet/blueprint-icons";
import { BTButton, type IBTButtonProps } from "@btnet/core-components/BTButton";
import { BTText } from "@btnet/core-components/BTText";
import { cn } from "@btnet/utilities-core";

import "./BTFilterButton.less";

const getFilterCountString = (filterCount?: number) => {
    if (!filterCount || filterCount < 1) return undefined;
    return !!filterCount && filterCount > 99 ? "99+" : filterCount?.toString();
};

/**
 * BTFilterButton component to show filter count badge on filter button. This is a wrapper around BTButton.
 *
 * @param props - IBTButtonProps with an additional filterCount prop that indicates the number of active filters. If filterCount is greater than 0, a badge will be displayed on the button.
 * @returns JSX.Element - The rendered BTFilterButton component. This should be used within the BTListHeader component to provide filter functionality with visual feedback on active filters.
 */
export function BTFilterButton<ActionType>(
    props: IBTButtonProps<ActionType> & { filterCount?: number },
): JSX.Element {
    const { filterCount, ...buttonProps } = props;
    const filterCountStr = getFilterCountString(filterCount);

    return (
        <div className="bt-filter-button--wrapper">
            <BTButton<ActionType>
                {...buttonProps}
                className={cn("bt-filter-button", props.className)}
                type={props.type || "tertiary"}
                icon={props.icon || <IconFunnel />}
            />
            {/* Only show badge if filterCount is greater than 0 and the button is not loading */}
            {filterCountStr && !props.loading && (
                <span
                    className={cn("bt-filter-button--badge", {
                        "bt-filter-button--badge-disabled": props.disabled,
                    })}
                    aria-label={`${filterCountStr} ${
                        filterCount === 1 ? "filter" : "filters"
                    } applied`}
                >
                    <BTText className="bt-filter-button--badge-text">{filterCountStr}</BTText>
                </span>
            )}
        </div>
    );
}
